import { useEffect, useRef, useState } from "react";
import { Plus, Building2, Home, MapPin, Bed, Bath, ExternalLink, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { resolveImageUrl } from "@/utils/imageUrl";
import { MilestonesProgressStrip } from "./MilestonesProgressStrip";
import { RendyVideoStatusBadge } from "./RendyVideoStatusBadge";
import useSiteStyles from "@/hooks/useSiteStyles";
import MilestonesDashboardModule from "@/components/modules/MilestonesDashboardModule";

// Whether the inline milestones panel starts expanded or collapsed — a
// single site-wide preference (not per-property) so it stays put as the
// agent switches between their properties.
const MILESTONES_EXPANDED_KEY = "slh_milestones_dashboard_expanded";

const formatPrice = (price, currency = "£") => {
  if (!price && price !== 0) return "Price on Application";
  try {
    return `${currency}${Number(price).toLocaleString()}`;
  } catch {
    return `${currency}${price}`;
  }
};

/** One card in the property navigation row — clicking it opens the Edit Property portal directly. */
function PropertyNavCard({ prop, isActiveEditing, offerBadge, onEdit, viewEnabled, editEnabled, isSingleProperty }) {
  const imgSrc = prop.main_image ? resolveImageUrl(prop.main_image) : null;
  const clickable = editEnabled && !isSingleProperty;
  const CardTag = clickable ? "button" : "div";
  return (
    <div
      className={`w-full h-full min-w-0 flex flex-col text-left border transition-all relative ${
        isActiveEditing && !isSingleProperty ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border hover:border-primary/50"
      }`}
      data-testid={`property-nav-card-${prop.property_id}`}
    >
      <CardTag
        type={clickable ? "button" : undefined}
        onClick={clickable ? () => onEdit(prop) : undefined}
        className={`w-full text-left ${clickable ? "" : "cursor-default"}`}
        data-testid={`property-cta-edit-${prop.property_id}`}
      >
        <div className="relative w-full h-20 flex-shrink-0 bg-muted overflow-hidden">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={prop.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Home className="h-6 w-6 text-muted-foreground/40" />
            </div>
          )}
          <span
            className={`absolute top-1.5 right-1.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${
              prop.is_live ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
            }`}
          >
            {prop.is_live ? "LIVE" : "DRAFT"}
          </span>
          {isActiveEditing && !isSingleProperty && (
            <span className="absolute top-1.5 left-1.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap bg-primary text-primary-foreground">
              EDITING
            </span>
          )}
        </div>
        <div className="p-2.5 flex-1 flex flex-col">
          <h4 className="font-serif text-sm font-medium truncate">{prop.title || "Untitled Home"}</h4>
          {prop.display_address && (
            <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
              <MapPin size={10} className="flex-shrink-0" />
              <span className="truncate">{prop.display_address}</span>
            </p>
          )}
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-1 flex-wrap">
            {prop.price > 0 && (
              <span className="font-semibold text-foreground">{formatPrice(prop.price, prop.currency || "£")}</span>
            )}
            {prop.beds > 0 && (
              <span className="flex items-center gap-0.5"><Bed size={10} /> {prop.beds}</span>
            )}
            {prop.baths > 0 && (
              <span className="flex items-center gap-0.5"><Bath size={10} /> {prop.baths}</span>
            )}
          </div>
          {(offerBadge || prop.rendy_video_status) && (
            <div className="flex items-center gap-1 flex-wrap self-start mt-auto pt-1.5">
              {offerBadge && (
                <span className={`inline-block text-[9px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap ${offerBadge.cls}`}>
                  {offerBadge.label}
                </span>
              )}
              <RendyVideoStatusBadge status={prop.rendy_video_status} progress={prop.rendy_video_progress} />
            </div>
          )}
        </div>
      </CardTag>

      {/* Quick link — opens the live page in a new tab without entering the editor.
          Hidden for a single-home portfolio, where View/Edit move up next to
          the "My Home" heading instead (nothing to disambiguate between cards). */}
      {!isSingleProperty && viewEnabled && prop.is_live && (
        <div className="px-2.5 pb-2.5 pt-1 border-t border-border/60">
          <a
            href={`/property/${prop.property_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-full h-6 text-[10px] hover:bg-muted transition-colors"
            data-testid={`property-cta-view-${prop.property_id}`}
          >
            <ExternalLink className="h-3 w-3 mr-1" /> View
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * "My Properties" tab — a full-width property navigation row (up to 6 cards
 * per page) that selects a property, plus a shared CTA toolbar below that
 * always applies to whichever property is currently selected. Extracted
 * from PropertyAdminDashboard.jsx to keep that file's size manageable.
 */
export function PropertyCardsGrid({
  linkedProperties,
  propertyData,
  receivedOffers,
  switchingProperty,
  setActiveTab,
  handleSwitchProperty,
  openPropertyModal,
  setShowNewPropertyDialog,
}) {
  const scrollRowRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { styles: siteStylesData } = useSiteStyles();
  const addPropertyEnabled = siteStylesData?.addPropertyEnabled !== false;
  const dashboardEnabled = siteStylesData?.dashboardEnabled !== false;
  const viewEnabled = siteStylesData?.ctaViewEnabled !== false;
  const editEnabled = siteStylesData?.ctaEditEnabled !== false;
  const [milestonesExpanded, setMilestonesExpanded] = useState(() => {
    try { return window.localStorage.getItem(MILESTONES_EXPANDED_KEY) === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(MILESTONES_EXPANDED_KEY, milestonesExpanded ? "1" : "0"); }
    catch { /* ignore quota */ }
  }, [milestonesExpanded]);

  // Track scroll position so the edge fades only show when there's actually more to scroll.
  useEffect(() => {
    const el = scrollRowRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    update();
    el.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [linkedProperties]);

  const offerBadgeFor = (prop) => {
    const offers = receivedOffers.filter((o) => o.property_id === prop.property_id);
    const openCount = offers.filter((o) => (o.status || "sent") === "sent").length;
    const hasAccepted = offers.some((o) => o.status === "accepted");
    const hasRejected = offers.some((o) => o.status === "rejected");
    if (hasAccepted) return { label: `Accepted (${offers.length})`, cls: "bg-green-100 text-green-800 border-green-200" };
    if (openCount > 0) return { label: `${openCount} new`, cls: "bg-amber-100 text-amber-800 border-amber-200 animate-pulse" };
    if (hasRejected) return { label: `${offers.length} rejected`, cls: "bg-red-50 text-red-700 border-red-200" };
    return null;
  };

  const handleEdit = async (prop) => {
    if (!prop || switchingProperty) return;
    if (prop.property_id !== propertyData?.property_id) {
      await handleSwitchProperty(prop.property_id);
    }
    document.getElementById("home-dashboard-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const isSingleProperty = linkedProperties.length === 1;
  const singleProp = isSingleProperty ? linkedProperties[0] : null;
  const scrollToEditSection = () => {
    document.getElementById("edit-home-details-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-white border border-border p-6" data-testid="my-properties-list">
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
        <div>
          <h3 className="font-serif text-xl font-medium">{isSingleProperty ? "My Home" : "My Homes"}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {linkedProperties.length} {linkedProperties.length === 1 ? "home" : "homes"} in your portfolio
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Single-home portfolio: View/Edit move up here instead of living on the card below. */}
          {isSingleProperty && viewEnabled && singleProp?.is_live && (
            <a
              href={`/property/${singleProp.property_id}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="my-home-view-btn"
            >
              <Button type="button" variant="outline" className="rounded-none">
                <ExternalLink className="h-4 w-4 mr-2" />
                View
              </Button>
            </a>
          )}
          {isSingleProperty && editEnabled && (
            <Button
              type="button"
              variant="outline"
              onClick={scrollToEditSection}
              className="rounded-none"
              data-testid="my-home-edit-btn"
            >
              Edit
            </Button>
          )}
          {addPropertyEnabled && (
            <Button
              onClick={() => setShowNewPropertyDialog(true)}
              className="rounded-none"
              data-testid="my-properties-new-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Home
            </Button>
          )}
        </div>
      </div>

      {linkedProperties.length === 0 ? (
        <div className="text-center py-16">
          <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h4 className="font-serif text-lg font-medium mb-2">No homes yet</h4>
          <p className="text-sm text-muted-foreground mb-6">
            Get started by creating your first home listing.
          </p>
          {addPropertyEnabled && (
            <Button onClick={() => setShowNewPropertyDialog(true)} className="rounded-none">
              <Plus className="h-4 w-4 mr-2" />
              Create First Home
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Property navigation row — scrolls horizontally, showing 3 cards at a time on mobile / 6 on desktop */}
          <div className="relative">
            <div
              className={`pointer-events-none absolute left-0 top-0 bottom-1 w-8 bg-gradient-to-r from-white to-transparent z-10 transition-opacity duration-200 ${
                canScrollLeft ? "opacity-100" : "opacity-0"
              }`}
              data-testid="property-nav-fade-left"
            />
            <div
              ref={scrollRowRef}
              className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 -mx-1 px-1"
              data-testid="property-nav-row"
            >
              {linkedProperties.map((prop) => (
                <div
                  key={prop.property_id}
                  className={isSingleProperty ? "flex-none w-full sm:w-64" : "flex-none snap-start w-[calc((100%-24px)/3)] sm:w-[calc((100%-60px)/6)]"}
                >
                  <PropertyNavCard
                    prop={prop}
                    isActiveEditing={prop.property_id === propertyData?.property_id}
                    offerBadge={offerBadgeFor(prop)}
                    onEdit={handleEdit}
                    viewEnabled={viewEnabled}
                    editEnabled={editEnabled}
                    isSingleProperty={isSingleProperty}
                  />
                </div>
              ))}
            </div>
            <div
              className={`pointer-events-none absolute right-0 top-0 bottom-1 w-8 bg-gradient-to-l from-white to-transparent z-10 transition-opacity duration-200 ${
                canScrollRight ? "opacity-100" : "opacity-0"
              }`}
              data-testid="property-nav-fade-right"
            />
          </div>

          {/* Graphical milestones summary — click expands/collapses the full
              milestones panel inline below it (no modal), each milestone on
              its own line; the expand/collapse choice persists across visits.
              Selecting a different home card scrolls here (see handleEdit). */}
          <div id="home-dashboard-section" className="scroll-mt-40">
          {dashboardEnabled && (
            <MilestonesProgressStrip
              propertyId={propertyData?.property_id}
              expanded={milestonesExpanded}
              onClick={() => setMilestonesExpanded((v) => !v)}
            />
          )}
          {dashboardEnabled && milestonesExpanded && propertyData?.property_id && (
            <div className="relative mt-3 border border-[#E5E5E0] bg-white" data-testid="milestones-expanded-panel">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-3 right-3 z-10 h-8 w-8"
                onClick={() => setMilestonesExpanded(false)}
                title="Close expanded view"
                data-testid="milestones-expanded-close-btn"
              >
                <X className="h-4 w-4" />
              </Button>
              <MilestonesDashboardModule key={propertyData.property_id} config={{}} initialPropertyId={propertyData.property_id} />
            </div>
          )}
          </div>
        </>
      )}
    </div>
  );
}

export default PropertyCardsGrid;
